import { t } from '@sm/intl';

import { getCookieValue, isBrowser, EMPTY_STRING } from '~shared/util';

import COPY from '~pricing/pages/Enterprise/copy';

import {
  HsContext,
  HsConsent,
  HsFormFields,
  HsFormValues,
  HubspotDTO,
  HsCommunicationDTO,
  HubspotApiReturnStatus,
  FormState,
  ValidationColor,
  SubmissionDataOptions,
} from './types';
import { isFormValid, ERROR_MAP } from './Validation';

const ENDPOINT = 'https://api.hsforms.com/submissions/v3/integration/submit';

const DEFAULT_HUBSPOT_ROUTE = '/5811593/289edeb2-bb6b-45f7-bef3-7185f3c6e9f4';
const MONTHLY_SUB_ID = 9074378;
const PRODUCT_UPDATES_SUB_ID = 9074384;

const HS_ERROR_STATUS: string[] = ['error'];

/**
 * Utility to retrieve hubspot Utility values
 * @returns HsContext
 */
export const getHubspotContextValues = (): HsContext => ({
  hutk: getCookieValue('hubspotutk'),
  pageName: isBrowser() ? document.title : '',
  pageUri: isBrowser() ? document.location.pathname : '',
});

/**
 * Maps the name and value for given set of fields
 * @param fields
 * @param formData
 * @returns key value pair for hubspot submission values
 */
function mapFieldAndValues(fields: HsFormFields[], formData: FormState): HsFormValues[] {
  return fields?.map(({ name, defaultValue }): HsFormValues => {
    const formDTO = formData[name];
    return {
      name,
      value: formDTO ? formDTO.value : defaultValue,
    };
  });
}

/**
 * Sanitizes Hubspot form data for submission
 * @param formFields
 * @param formData
 * @returns HubspotDTO | undefined
 */
function mapHubspotSubmissionData(formFields: HsFormFields[], formData: FormState): HubspotDTO | undefined {
  const communications: HsCommunicationDTO[] = [
    {
      text: t(COPY.CONSENT_FOR_SUBMISSION),
      subscriptionTypeId: MONTHLY_SUB_ID,
      value: !!formData?.consent?.value,
    },
    {
      text: t(COPY.CONSENT_FOR_SUBMISSION),
      subscriptionTypeId: PRODUCT_UPDATES_SUB_ID,
      value: !!formData?.consent?.value,
    },
  ];
  const legalConsentOptions: HsConsent = {
    consent: {
      communications,
      consentToProcess: true,
      text: EMPTY_STRING,
    },
  };
  return {
    context: getHubspotContextValues(),
    fields: mapFieldAndValues(formFields, formData),
    legalConsentOptions,
  } as HubspotDTO;
}
/**
 * A fetch helper function to submit Hubspot forms
 * @param dataOptions
 * @returns Promise<HubspotApiReturnStatus>
 */
export async function saveFormData(
  dataOptions: SubmissionDataOptions,
  autoSubmit = false,
  apiRoute: string = DEFAULT_HUBSPOT_ROUTE
): Promise<HubspotApiReturnStatus | undefined> {
  const { formFields, formData } = dataOptions;
  // bail right away if the overall form lacks a required field
  if (!isFormValid(formData) && !autoSubmit) {
    return;
  }
  const submitData = mapHubspotSubmissionData(formFields, formData);
  const url = `${ENDPOINT}/${apiRoute}`;
  const response =
    submitData &&
    (await fetch(url, {
      method: 'POST',
      body: JSON.stringify(submitData),
      headers: {
        'Content-type': 'application/json;charset=utf-8',
      },
    }));
  return response?.json();
}

/**
 * Parses and validates Hubspot server side error
 * @param responseData
 * @param formData
 * @returns FormState | undefined
 */
export function parseHubspotApiResponse(
  responseData: HubspotApiReturnStatus,
  formData: FormState
): FormState[] | undefined {
  const { status = EMPTY_STRING, errors = [] } = responseData;
  if (HS_ERROR_STATUS.includes(status)) {
    return errors.map(({ message, errorType }): FormState => {
      const [field] = message.match(/(fields|context)\.([^']*)/) as RegExpMatchArray; // modified version from cms
      const fieldName = field?.replace(/(.*)\./, ''); // retrieves the field name from the above string
      const mapFormDataWWithError: FormState = {
        [fieldName]: {
          value: formData[fieldName].value,
          color: ValidationColor.error,
          errorMessage: ERROR_MAP[errorType],
        },
      };
      return mapFormDataWWithError;
    });
  }
}
